import React, { useEffect, useState } from "react";
import { Box, Tab, Tabs, TablePagination, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { getById, getByPlacekey } from "../../utilities/locationService";
import CommonDialog from "../../commondialog/confirm";
import RelanceCredit, { CreditEchecBadge } from "../../components/CreditEchec";
import IntegriteBadges from "../../components/IntegriteBadges";
import EnTetePage from "../../components/charte/EnTetePage";
import TitreSection from "../../components/charte/TitreSection";
import { BoutonPrincipal, BoutonSecondaire } from "../../components/charte/Boutons";
import { Indicateur3 } from "../../components/charte/TroisBarres";
import { COULEURS, COULEUR_DOMAINE } from "../../theme/tribeTheme";
import { chargerAttente, estEnAttente, invaliderAttente, lireDate, niveauUrgence, useAttente } from "../../navigation/attente";
import { Data, FicheLieu, ONGLETS, anciennete, useFicheLieu, useFileLieux } from "./logiqueLieux";
import { AnalysePhoto, CorroborationBadge, FicheLieuModal, ProximityBadge, StatusBadge } from "./composantsLieux";
import { AIDE_LIEUX } from "../../aide/textesAide";
import BoutonAide from "../../components/charte/BoutonAide";

/* ══════════════════════════════════════════════════════════════════════════
   À traiter › Lieux à valider (et Lieux › Fichier des lieux, même écran
   ouvert sur un autre onglet). Une fiche par lieu ; mêmes actions et mêmes
   appels que l'ancienne file de pages/location.tsx (logiqueLieux.ts) :
   approbation, rejet avec motif du référentiel, approbation d'une sélection
   de lieux corroborés, masquage avec motif, réaffichage, relance d'un crédit
   en échec, analyse photo à la demande. Les photos, l'analyse et les
   contrôles d'intégrité d'une fiche sont lus par l'appel de la fiche
   (GET /location/getbyid), lieu par lieu, à l'affichage.
   ══════════════════════════════════════════════════════════════════════════ */

const LIBELLES_ONGLETS: Record<string, string> = {
  attente: "En attente",
  verifie: "Vérifiés (corroborés)",
  reponses: "Réponses aux quêtes",
  approuve: "Approuvés",
  rejete: "Rejetés",
  expire: "Expirés",
  masque: "Masqués",
};

/** Détail d'un lieu lu par l'appel de la fiche : photo, analyse, intégrité. */
function useDetailLieu(row: Data) {
  const [detail, setDetail] = useState<{ photo?: string; verdict?: any; integrite?: any; exif?: any; erreur?: boolean } | null>(null);
  useEffect(() => {
    let actif = true;
    setDetail(null);
    const appel = row.placekey ? getByPlacekey(row.placekey) : row.referenceId ? getById(row.referenceId) : null;
    if (!appel) return;
    appel.then((res: any) => {
      const d: any = Array.isArray(res) ? res[0] : (res?.data ?? res);
      if (!actif || !d) return;
      setDetail({ photo: d.images?.[0]?.image_content, verdict: d.verdictVision, integrite: d.signauxIntegrite, exif: d.signauxExif });
    }).catch(() => actif && setDetail({ erreur: true }));
    return () => { actif = false; };
  }, [row.referenceId, row.placekey]);
  return [detail, setDetail] as const;
}

function FicheCarte({ row, onglet, fiche, file }: {
  row: Data; onglet: string; fiche: FicheLieu; file: ReturnType<typeof useFileLieux>;
}) {
  const [detail, setDetail] = useDetailLieu(row);
  const [refusOuvert, setRefusOuvert] = useState(false);
  const [motif, setMotif] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [confirmer, setConfirmer] = useState<null | "valider" | "refuser" | "masquer" | "reafficher">(null);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const ecrire = fiche.location_write;
  const decidable = onglet === "attente" || onglet === "verifie";
  const age = anciennete(row.createdOn);
  const dateCreation = lireDate(row.createdOn);
  const motifDef = fiche.motifs.find(m => m.code === motif);

  const analyser = async () => {
    setAnalyseEnCours(true);
    const v = await fiche.analyser(row);
    setAnalyseEnCours(false);
    if (v) setDetail(d => ({ ...(d ?? {}), verdict: v }));
  };

  return (
    <Box component="article" aria-label={row.Location_Name || "Lieu sans nom"} sx={{
      display: "grid", gridTemplateColumns: { xs: "1fr", md: "180px minmax(0,1fr) 250px" }, gap: 2,
      background: "var(--c-surface)", border: "1.5px solid var(--c-text-primary)", borderLeft: `6px solid ${COULEUR_DOMAINE.lieux}`,
      borderRadius: "8px", p: 2,
    }}>
      {/* Photo */}
      <Box sx={{ position: "relative" }}>
        {onglet === "verifie" && ecrire && (
          <Box component="label" sx={{ position: "absolute", top: 6, left: 6, zIndex: 1, background: "var(--c-surface)", border: "1.5px solid var(--c-text-primary)", borderRadius: "6px", px: 0.75, py: 0.25, display: "flex", alignItems: "center", gap: 0.5, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <input type="checkbox" checked={file.selectionVerifies.includes(row.referenceId)}
              onChange={() => file.basculerSelection(row.referenceId)} aria-label={`Sélectionner ${row.Location_Name}`} />
            Sélection
          </Box>
        )}
        <Box
          component="button" type="button" onClick={() => fiche.ouvrirFiche(row)}
          aria-label={`Ouvrir la fiche de ${row.Location_Name || "ce lieu"}`}
          sx={{ width: "100%", height: { xs: 180, md: 150 }, p: 0, border: "1px solid var(--c-border)", borderRadius: "6px", overflow: "hidden", background: "var(--c-surface-3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {detail?.photo
            ? <img src={`data:image/png;base64,${detail.photo}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>{detail == null ? "Photo…" : detail.erreur ? "Photo illisible" : "Sans photo"}</Typography>}
        </Box>
      </Box>

      {/* Informations et contrôles */}
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
          <Typography component="h3" sx={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17 }}>
            {row.Location_Name || "Lieu sans nom"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--c-text-secondary)" }}>{row.Category || "Type non renseigné"}</Typography>
        </Box>
        <Typography sx={{ fontSize: 13.5 }}>{row.Location_Address || "Adresse non renseignée"}</Typography>
        <Typography sx={{ fontSize: 12.5, color: "var(--c-text-secondary)" }}>
          Spotter : {row.createdBy || "—"} · {age ? `envoyé ${age}` : "date d'envoi inconnue"}
          {dateCreation ? ` (${dateCreation.toLocaleDateString("fr-FR")})` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
          {!decidable && <StatusBadge status={row.Status} />}
          <CorroborationBadge nb={row.nbCorroborations} />
          <ProximityBadge distance={row.photo_distance} conforme={row.photo_conforme} />
          {row.creditEchec && <CreditEchecBadge message={row.creditEchecMessage} date={row.creditEchecLe} />}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 0.5 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "var(--c-text-secondary)" }}>Analyse photo</Typography>
              <BoutonAide texte={AIDE_LIEUX.analysePhoto} />
            </Box>
            <AnalysePhoto verdict={detail?.verdict ?? (row.verdict ? { verdict: row.verdict } : null)} compact />
            {ecrire && (
              <BoutonSecondaire size="small" onClick={analyser} disabled={analyseEnCours} sx={{ mt: 0.75, py: 0.1, px: 1, fontSize: 12 }}>
                {analyseEnCours ? "Analyse…" : "Analyser la photo"}
              </BoutonSecondaire>
            )}
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "var(--c-text-secondary)" }}>Contrôles d'intégrité</Typography>
              <BoutonAide texte={AIDE_LIEUX.integrite} />
            </Box>
            {detail == null
              ? <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>Lecture…</Typography>
              : <IntegriteBadges signaux={detail.integrite} exif={detail.exif} />}
          </Box>
        </Box>
      </Box>

      {/* Tips et décision */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, borderLeft: { md: "1px solid var(--c-border)" }, pl: { md: 2 } }}>
        <Box>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "var(--c-text-secondary)" }}>Tips en attente</Typography>
          <Typography sx={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 22 }}>
            {row.aCrediter == null ? "—" : `${row.aCrediter.toLocaleString("fr-FR")} Tips`}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "var(--c-text-secondary)" }}>crédités à la validation (découvreur et corroborateurs)</Typography>
        </Box>

        {ecrire && decidable && !refusOuvert && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <BoutonPrincipal onClick={() => setConfirmer("valider")} disabled={row.aCrediter == null} sx={{ flex: 1 }}>Valider</BoutonPrincipal>
            <BoutonSecondaire onClick={() => setRefusOuvert(true)} sx={{ flex: 1 }}>Refuser</BoutonSecondaire>
          </Box>
        )}
        {ecrire && decidable && refusOuvert && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <label htmlFor={`motif-${row.referenceId}`} style={{ fontSize: 12.5, fontWeight: 600 }}>Motif du refus (obligatoire)</label>
            <select id={`motif-${row.referenceId}`} value={motif} onChange={e => setMotif(e.target.value)} style={{ padding: "7px 8px", width: "100%" }}>
              <option value="">— choisir un motif —</option>
              {fiche.motifs.map(m => <option key={m.code} value={m.code}>{m.libelle}</option>)}
            </select>
            <textarea value={commentaire} onChange={e => setCommentaire(e.target.value)}
              placeholder={motifDef?.commentaireObligatoire ? "Commentaire (obligatoire pour ce motif)" : "Commentaire (facultatif)"}
              style={{ padding: "7px 8px", minHeight: 44, width: "100%" }} />
            <Box sx={{ display: "flex", gap: 1 }}>
              <BoutonPrincipal onClick={() => setConfirmer("refuser")} disabled={!motif} sx={{ flex: 1, px: 1 }}>Confirmer le refus</BoutonPrincipal>
              <BoutonSecondaire onClick={() => { setRefusOuvert(false); setMotif(""); setCommentaire(""); }} sx={{ px: 1.25 }}>Annuler</BoutonSecondaire>
            </Box>
          </Box>
        )}
        {row.creditEchec && (
          <RelanceCredit type="poi" id={row.referenceId || row.placekey!} message={row.creditEchecMessage} date={row.creditEchecLe}
            peutRelancer={ecrire} compact onRelance={() => file.recharger()} />
        )}
        <Box sx={{ display: "flex", gap: 1.5, mt: "auto", pt: 0.5, flexWrap: "wrap" }}>
          <Box component="button" type="button" onClick={() => fiche.ouvrirFiche(row)} sx={lienBouton}>Voir la fiche complète</Box>
          {ecrire && (onglet === "masque"
            ? <Box component="button" type="button" onClick={() => setConfirmer("reafficher")} sx={lienBouton}>Réafficher</Box>
            : <Box component="button" type="button" onClick={() => setConfirmer("masquer")} sx={{ ...lienBouton, color: COULEURS.erreur }}>Masquer</Box>)}
        </Box>
      </Box>

      <CommonDialog
        open={confirmer === "valider"}
        handleClose={() => setConfirmer(null)}
        handleConfirm={() => { setConfirmer(null); fiche.approuverLieu(row); }}
        contentText={`Approuver ce lieu crédite ${(row.aCrediter ?? 0)} Tips (découvreur et corroborateurs non encore payés). Le crédit est définitif.`}
      />
      <CommonDialog
        open={confirmer === "refuser"}
        handleClose={() => setConfirmer(null)}
        handleConfirm={async () => { setConfirmer(null); if (await fiche.rejeterLieu(row, motif, commentaire)) setRefusOuvert(false); }}
        contentText="Rejeter ce lieu ? Le motif choisi est conservé au journal des décisions."
      />
      <CommonDialog
        open={confirmer === "masquer"}
        handleClose={() => setConfirmer(null)}
        handleConfirm={() => { setConfirmer(null); file.handleLocationDelete(row.referenceId); }}
        contentText="Masquer ce lieu ? Un motif vous sera demandé ; il est conservé au journal des décisions."
      />
      <CommonDialog
        open={confirmer === "reafficher"}
        handleClose={() => setConfirmer(null)}
        handleConfirm={() => { setConfirmer(null); file.handleReafficher(row.referenceId); }}
        contentText="Réafficher ce lieu ? Il revient dans la file de validation et sur la carte. La décision est conservée au journal."
      />
    </Box>
  );
}

const lienBouton = {
  background: "none", border: "none", p: 0, font: "inherit", fontSize: 13, fontWeight: 600,
  color: "var(--c-text-primary)", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer",
} as const;

// ── Réponses aux quêtes en attente ─────────────────────────────────────────

const urlPhotoSure = (url?: string) => url && /^(https?:\/\/|data:image\/)/i.test(url.trim()) ? url.trim() : null;

function ReponsesQuetes() {
  const attente = useAttente();
  useEffect(() => { chargerAttente(true); }, []);
  const enAttente = attente.soumissions.filter(estEnAttente)
    .sort((a, b) => (lireDate(a.createdAt)?.getTime() ?? 0) - (lireDate(b.createdAt)?.getTime() ?? 0));
  const parQuete = enAttente.reduce<Record<string, any[]>>((acc, s) => { (acc[s.questId] ??= []).push(s); return acc; }, {});

  if (!attente.charge && enAttente.length === 0) {
    return <Typography sx={{ color: "var(--c-text-secondary)", fontSize: 14 }}>Lecture des réponses aux quêtes…</Typography>;
  }
  if (enAttente.length === 0) {
    return <Typography sx={{ color: "var(--c-text-secondary)", fontSize: 14 }}>Aucune réponse de quête en attente.</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography sx={{ fontSize: 13.5, color: "var(--c-text-secondary)" }}>
        La validation d'une réponse se fait dans sa quête, où se trouvent la carte des collectes, les motifs de refus et le crédit des Tips.
      </Typography>
      {Object.entries(parQuete).map(([questId, subs]) => {
        const plusAncien = lireDate(subs[0]?.createdAt);
        return (
          <Box key={questId} component="section">
            <TitreSection
              titre={subs[0]?.questTitre || "Quête"}
              sousTitre={`${subs.length} en attente`}
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Indicateur3 niveau={niveauUrgence(plusAncien)} libelle="Urgence" detail={plusAncien ? `plus ancienne ${anciennete(plusAncien.getTime())}` : "ancienneté inconnue"} />
                  <BoutonPrincipal component={RouterLink as any} to={`/quetes/${questId}`}>Décider dans la quête</BoutonPrincipal>
                </Box>
              }
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0,1fr))", xl: "repeat(3, minmax(0,1fr))" }, gap: 1.5 }}>
              {subs.map((s, i) => {
                const photo = urlPhotoSure(s.photoUrl);
                return (
                  <Box key={s.id ?? i} sx={{ display: "flex", gap: 1.5, background: "var(--c-surface)", border: "1px solid var(--c-border)", borderLeft: `6px solid ${COULEUR_DOMAINE.quetes}`, borderRadius: "8px", p: 1.5 }}>
                    <Box sx={{ width: 88, height: 72, flexShrink: 0, borderRadius: "6px", overflow: "hidden", background: "var(--c-surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {photo ? <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Typography sx={{ fontSize: 11, color: "var(--c-text-secondary)" }}>Sans photo</Typography>}
                    </Box>
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{s.collecteurNom || s.collecteurEmail || "Spotter"}</Typography>
                      <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>
                        {anciennete(lireDate(s.createdAt)?.getTime()) ? `reçue ${anciennete(lireDate(s.createdAt)!.getTime())}` : "date inconnue"}
                      </Typography>
                      <AnalysePhoto verdict={s.verdictVision} compact />
                      <IntegriteBadges signaux={s.signauxIntegrite} exif={s.signauxExif} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

export default function LieuxAValider({ vue = "validation" }: { vue?: "validation" | "fichier" }) {
  const [parametres, setParametres] = useSearchParams();
  const ongletsVue = vue === "validation"
    ? ["attente", "verifie", "reponses", "approuve", "rejete", "expire", "masque"]
    : ["approuve", "rejete", "expire", "masque", "attente", "verifie"];
  const demande = parametres.get("onglet") ?? "";
  const ongletCourant = ongletsVue.includes(demande) ? demande : ongletsVue[0];
  const reponses = ongletCourant === "reponses";
  const indexLieux = ONGLETS.indexOf((reponses ? "attente" : ongletCourant) as any);

  const file = useFileLieux(indexLieux, invaliderAttente);
  const fiche = useFicheLieu(() => file.recharger());
  const attente = useAttente();
  const [recapOuvert, setRecapOuvert] = useState(false);

  // L'onglet suit l'adresse (?onglet=…), pour les liens et le bouton Précédent.
  useEffect(() => {
    if (!reponses && indexLieux !== file.valTab) { file.setValTab(indexLieux); file.setPage(0); file.setSearchQuery(""); }
  }, [indexLieux, reponses]);
  const changerOnglet = (o: string) => setParametres(o === ongletsVue[0] ? {} : { onglet: o }, { replace: true });

  const compte = (o: string) => o === "reponses" ? attente.lignes.reponses?.nombre : (file.compteurs as any)[o];

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1320 }}>
      <EnTetePage
        domaine={vue === "validation" ? "traiter" : "lieux"}
        titre={vue === "validation" ? "Lieux à valider" : "Fichier des lieux"}
        phrase={vue === "validation"
          ? "Une fiche par lieu envoyé : vérifiez la photo, l'analyse et les contrôles, puis validez ou refusez."
          : "Tous les lieux collectés, par statut. Chaque fiche garde ses actions : réouvrir, masquer, réafficher."}
        aide={vue === "validation" ? AIDE_LIEUX.aValider : AIDE_LIEUX.fichier}
      />

      <Tabs
        value={ongletCourant}
        onChange={(_, v) => changerOnglet(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2.5, borderBottom: "1px solid var(--c-border)" }}
      >
        {ongletsVue.map(o => {
          const n = compte(o);
          return (
            <Tab key={o} value={o} label={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {LIBELLES_ONGLETS[o]}
                {n != null && <span style={{ border: "1px solid #1F1E1B", borderRadius: 5, fontSize: 11, fontWeight: 700, padding: "0 6px", lineHeight: 1.6 }}>{n}</span>}
              </span>
            } />
          );
        })}
      </Tabs>

      {reponses ? <ReponsesQuetes /> : (
        <>
          <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              value={file.searchQuery}
              onChange={e => { file.setSearchQuery(e.target.value); file.setPage(0); }}
              placeholder="Rechercher un lieu, une adresse, un type…"
              size="small"
              inputProps={{ "aria-label": "Rechercher" }}
              sx={{ flex: 1, minWidth: 220 }}
            />
            {/* Le périmètre (Sénégal) est fixé côté serveur (question 25). */}
            <select
              value={file.verdictFilter}
              onChange={e => { file.setVerdictFilter(e.target.value); file.setPage(0); }}
              aria-label="Filtrer par verdict de l'analyse photo"
              style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", minWidth: 180 }}
            >
              <option value="">Toutes les analyses</option>
              <option value="douteux">Douteux</option>
              <option value="non_conforme">Non conforme</option>
              <option value="conforme">Conforme</option>
              <option value="aucun">Non analysé</option>
            </select>
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={file.verdictTri} onChange={e => { file.setVerdictTri(e.target.checked); file.setPage(0); }} />
              Douteux en premier
            </label>
          </Box>

          {ongletCourant === "verifie" && fiche.location_write && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ color: "var(--c-text-secondary)", fontSize: "12.5px" }}>
                La sélection porte sur la page affichée uniquement ; elle est vidée en changeant de page.
              </Typography>
              <BoutonPrincipal onClick={() => setRecapOuvert(true)} disabled={file.lignesSelectionnees.length === 0}>
                Valider la sélection ({file.lignesSelectionnees.length})
              </BoutonPrincipal>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {file.rows.length === 0 && (
              <Typography sx={{ color: "var(--c-text-secondary)", fontSize: 14, py: 2 }}>
                {file.chargement ? "Lecture de la file…" : "Aucun lieu dans cet onglet."}
              </Typography>
            )}
            {file.rows.map(row => (
              <FicheCarte key={row.referenceId} row={row} onglet={ongletCourant} fiche={fiche} file={file} />
            ))}
          </Box>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={file.totalOnglet}
            rowsPerPage={file.rowsPerPage}
            page={file.page}
            onPageChange={(_, p) => file.setPage(p)}
            onRowsPerPageChange={e => { file.setRowsPerPage(+e.target.value); file.setPage(0); }}
            labelRowsPerPage="Lieux par page"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            sx={{ mt: 1.5, border: "1px solid var(--c-border)", borderRadius: "8px" }}
          />

          <CommonDialog
            open={recapOuvert}
            handleClose={() => setRecapOuvert(false)}
            handleConfirm={() => { setRecapOuvert(false); file.handleApproveSelection(); }}
            contentText={`Approuver ${file.lignesSelectionnees.length} lieu(x) : ${file.tipsSelection.toLocaleString("fr-FR")} Tips seront crédités au total (découvreurs et corroborateurs). Le crédit est définitif.`}
          />
        </>
      )}

      <FicheLieuModal fiche={fiche} onRelance={() => file.recharger()} />
    </Box>
  );
}
